import { useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './Button.jsx';
import { cn } from '../../lib/cn.js';

/**
 * Modal dialog. Renders when open=true.
 *
 * Usage:
 *   <Modal open={showConfirm} onClose={() => setShowConfirm(false)} title="Delete?">
 *     <p>Are you sure?</p>
 *     <div className="mt-6 flex gap-2 justify-end">
 *       <Button variant="secondary" onClick={close}>Cancel</Button>
 *       <Button variant="danger" onClick={handleDelete}>Delete</Button>
 *     </div>
 *   </Modal>
 */
export function Modal({ open, onClose, title, description, children, size = 'md' }) {
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handler);
            document.body.style.overflow = '';
        };
    }, [open, onClose]);

    if (!open) return null;

const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-ink/50 backdrop-blur-sm animate-in fade-in"
                onClick={onClose}
                aria-hidden
            />
            {/* Dialog — flex column with bounded height so the body scrolls
                instead of the whole page (or worse, content getting clipped). */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? 'modal-title' : undefined}
                className={cn(
                    'relative w-full card animate-in fade-in zoom-in-95 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)]',
                    sizeClasses[size],
                )}
            >
                {title && (
                    <div className="card-header flex items-start justify-between gap-4 flex-shrink-0">
                        <div className="min-w-0 flex-1">
                            <h2 id="modal-title" className="text-base font-semibold text-ink">{title}</h2>
                            {description && <p className="mt-1 text-sm text-ink-subtle">{description}</p>}
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-ink-muted hover:text-ink transition-colors"
                            aria-label="Close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                )}
                <div className="card-body overflow-y-auto flex-1 min-w-0">{children}</div>
            </div>
        </div>
    );
}
